
import { Brain } from "lucide-react";
import { Button } from "@/components/Button";

interface CurriculumItemProps {
  title: string;
  description: string;
  index: number;
}

const CurriculumItem = ({ title, description, index }: CurriculumItemProps) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0">
      <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
        <span className="font-bold text-primary">{index + 1}</span>
      </div>
    </div>
    <div>
      <h3 className="text-xl font-semibold mb-2">{title}</h3>
      <p className="text-white/70">{description}</p>
    </div>
  </div>
);

interface ProgramProps {
  title: string;
  duration: string;
  details: string;
}

const Program = ({ title, duration, details }: ProgramProps) => (
  <div className="p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-300 border border-white/10">
    <div className="flex justify-between items-center mb-2">
      <h4 className="font-semibold">{title}</h4>
      <span className="text-sm text-white/60">{duration}</span>
    </div>
    <p className="text-sm text-white/70">{details}</p>
  </div>
);

const EducationCurriculum = () => {
  const curriculumItems = [
    {
      title: "AI Fundamentals: How It Works",
      description: "Understanding the core technologies behind AI, machine learning, and large language models in simple, accessible terms."
    },
    {
      title: "Limitations of AI: What Can You Trust?",
      description: "Learning to recognize AI hallucinations, output errors, and when human expertise is still essential in legal work."
    },
    {
      title: "AI In Your Everyday Life",
      description: "Exploring how AI already impacts daily workflows and identifying opportunities to leverage these tools effectively."
    },
    {
      title: "Practical Uses of AI in the Legal Profession",
      description: "Examining specific legal applications from document review to research that can transform practice efficiency."
    }
  ];

  const programs = [
    {
      title: "Introductory Workshop",
      duration: "1 Day",
      details: "Perfect for legal teams new to AI technology"
    },
    {
      title: "Comprehensive Course",
      duration: "1 Week",
      details: "In-depth training on all core AI legal applications"
    },
    {
      title: "Advanced Specialization",
      duration: "2 Weeks",
      details: "Focused training for specific practice areas"
    },
    {
      title: "Executive Program",
      duration: "2 Days",
      details: "Strategic overview for managing partners and leadership"
    }
  ];

  return (
    <section className="py-16 md:py-24 px-6 relative">
      <div className="absolute top-1/4 right-1/4 w-72 h-72 bg-blue-600/10 rounded-full filter blur-[100px]"></div>
      <div className="absolute bottom-1/4 left-1/4 w-80 h-80 bg-primary/10 rounded-full filter blur-[120px]"></div>
      
      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="flex flex-col md:flex-row gap-12 items-center">
          <div className="flex-1 order-2 md:order-1">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Curriculum Overview</h2>
            <p className="text-lg text-white/80 mb-6">
              Our comprehensive curriculum covers the most important aspects of AI in legal practice:
            </p>
            
            <div className="space-y-6">
              {curriculumItems.map((item, index) => (
                <CurriculumItem 
                  key={index}
                  index={index}
                  title={item.title}
                  description={item.description}
                />
              ))}
            </div>
            
            <div className="mt-8">
              <Button variant="glass" size="lg">Download Full Curriculum</Button>
            </div>
          </div>
          
          <div className="flex-1 order-1 md:order-2">
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6">
              <div className="mb-6 flex items-center gap-4">
                <Brain className="h-8 w-8 text-primary" />
                <h3 className="text-2xl font-bold">Training Programs</h3>
              </div>
              
              <div className="space-y-4">
                {programs.map((program, index) => (
                  <Program 
                    key={index}
                    title={program.title}
                    duration={program.duration}
                    details={program.details}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EducationCurriculum;
